import { Response } from "components/actions/response";
import { Bundle } from "components/Bundle";
import { Component, DataHolderComponent, isDataAppendableComponent } from "components/components/Component";
import { ConfirmationDialog } from "components/confirmationDialog/confirmationDialog";
import * as itemFinderModal from "components/input/searcherWithCode/baseFunctionsFinderModal";
import { isItemFinderActionDefinition } from "components/input/searcherWithCode/searcherWithSingleCode/itemFinderModalAction";
import { ParametersDialog, ParametersDialogDefinition } from "components/parametersDialog/parametersDialog";
import $ from "jquery";
import { I18nMessage, I18nMessageDefinition } from "../i18n/I18nMessage";
import { BackEventType } from "./backEvent";
import { getServerAcceptCallback } from "./serverActionRequest";

/**
 * An action can be:
 * <li> Server backed or
 * <li> Callback backed
 *
 * An action can have a confirmation window
 *
 * An action must emit the following events:
 * <li>actionBegun | preAction
 * <li>actionRetrieved | postAction
 * <li>actionPerformed
 * <li>actionFailed
 */
export class Action {
    public definition: ActionDefinition;
    public _data: any;
    /**
     * Optional component that can contain data.
     * If available the action will use the data provided by the component.
     */
    public dataHolder: DataHolderComponent;
    /**
     * Optional component that fires the action.
     * It can be used to fire further events.
     */
    public sourceComponent: Component;
    public _acceptCallback;
    public done: ((r: Response) => void);

    constructor(definition: ActionDefinition, sourceComponent: Component) {
        this.definition = definition;
        this.sourceComponent = sourceComponent;
    }

    private addConstants(definition: ActionDefinition, data: any) {
        if (isActionWithConstantsDefinition(definition)) {
            // tslint:disable-next-line: forin
            for (const key in definition.constants) {
                data[key] = definition.constants[key];
            }
        }
    }

    public run(data: any, component?: DataHolderComponent) {
        this.dataHolder = component;
        $(this).trigger("actionbegun", [this, data]);
        this._data = data ?? {};

        this.addConstants(this.definition, this._data);

        this.manageFinderAction();

        this._acceptCallback = getServerAcceptCallback(this);
        if (isCallbackActionDefinition(this.definition)) {
            this._acceptCallback = this.encapsulateCallback(this.definition.callback, this._data);
        }
        if (isUnstackActionDefinition(this.definition)) {
            this._acceptCallback = this.unstackCallback;
        }
        if (isDataDispatchActionDefinition(this.definition)) {
            this._acceptCallback = this.dataDispatchCallback;
        }
        if (isUndoActionDefinition(this.definition)) {
            this._acceptCallback = this.undoCallback;
        }
        if (this.definition.askForChangeConfirmation === true) {
            const event = new CustomEvent("askForChangeConfirmation", {
                bubbles: true,
                detail: {
                    componentHolder: this.dataHolder,
                    callback: this._acceptCallback,
                },
            });
            document.dispatchEvent(event);
        } else if (isConfirmedActionDefinition(this.definition)) {
            const def = {
                confirmationMessages: [this.definition.confirmationMessage],
                confirmationAccept: this.definition.confirmationAccept,
                confirmationDeclineKey: this.definition.confirmationDeclineKey,
                acceptCallback: this._acceptCallback,
            };
            if (this._data != null
                && isTableActionDefinition(this.definition)) {
                const actionData = this._data.data[this.definition.column];
                if ((actionData == null || actionData.length === 0) && this._data.allCheckedColumns[this.definition.column] !== true) {
                    this._acceptCallback();
                    return;
                }
                def.confirmationMessages[0].args = def.confirmationMessages[0].args || {};
                const totalRegsSelected = this.calculateTotalRegsSelected(this.definition.column, actionData);
                def.confirmationMessages[0].args["count"] = totalRegsSelected;
                if (def.confirmationAccept != null) {
                    def.confirmationAccept.args["count"] = totalRegsSelected;
                }
            }
            const dialog = new ConfirmationDialog(def);
            dialog.open();
        } else if (isParameterizedActionDefinition(this.definition)) {
            const def: ParametersDialogDefinition = Object.assign(this.definition);
            def.acceptCallback = this._acceptCallback;
            const dialog = new ParametersDialog(def);
            dialog.open();
        } else {
            this._acceptCallback();
        }
    }

    private calculateTotalRegsSelected(column: string, actionData: any) : number {
        let totalRegsSelected = 0;
        if(this._data.allCheckedColumns[column] === true) {
            totalRegsSelected = this._data.recordsTotal - actionData.length;
        } else {
            totalRegsSelected = actionData.length;
        }
        // No se puede formatear el nnumero pq se pierde la plurarlidad en los msg de i18ntext
        // const formatTotalRegsSelected = this.doFormat(totalRegsSelected);
        return totalRegsSelected;
    }

    private manageFinderAction() {
        if (isItemFinderActionDefinition(this.definition)) {
            if (isDataAppendableComponent(this.dataHolder)) {
                itemFinderModal.open(
                    itemFinderModal.EnumSearcherWithType.SINGLE,
                    { ...this.definition.finder, outputParameterMappings: this.definition.outputParameterMappings },
                    this.definition.queryParameters,
                    (data) => {
                        if (Array.isArray(data)) {
                            for (const d of data) {
                                d["table-check"] = true;
                            }
                        } else {
                            data["table-check"] = true;
                        }
                        if (isDataAppendableComponent(this.dataHolder)) {
                            this.dataHolder.appendData(data);
                        }
                    });
            }
        }
    }

    private encapsulateCallback = (callback, data) => {
        return () => {
            try {
                callback(data);
                $(this).trigger("actionRetrieved", [this, data]);
                $(this).trigger("actionPerformed", [this, data]);
            } catch (e) {
                console.warn(e);
                $(this).trigger("actionRetrieved", [this, data]);
                $(this).trigger("actionFailed", [this, data]);
            }
        };
    }

    private dataDispatchCallback = () => {
        this.unstackCallback();
        if (isDataDispatchActionDefinition(this.definition)) {
            const targetComponent = this.definition.targetComponent;
            const e = this.findTargetElement(targetComponent);

            if (e != null) {
                e.dispatchEvent(new CustomEvent(
                    "tableDataUpdateRequested",
                    {
                        detail: {
                            target: targetComponent,
                            data: this._data,
                        },
                    }),
                );
            }
        }
    }
    private findTargetElement(targetId: string): Element {
        return document.querySelector("[data-s6-id='" + targetId + "']");
    }
    private unstackCallback = () => {
        this.triggerEvent("pageUnstackRequested", {
            askForChangeConfirmation: this.definition.askForChangeConfirmation,
        });
    }

    private undoCallback = () => {
        this.triggerEvent("formUndoRequested");
    }

    public triggerEvent(eventName: string, detail: any = null) {
        const event = new CustomEvent(eventName, { bubbles: true, detail });
        if (this.sourceComponent == null) {
            this.dataHolder["view"].dispatchEvent(event);
        } else {
            this.sourceComponent["view"].dispatchEvent(event);
        }
    }
}

document.addEventListener("formUndoRequested", (evt: CustomEvent) => evt.detail.undo());

export interface FieldParameterMapping {
    field: string;
    as: string;
}

export interface ActionDefinition {
    // By default an action is a server action
    actionType?: string;
    actionName: string;
    needsChange?: boolean;
    needsValid?: boolean;
    askForChangeConfirmation?: boolean;
    submitsUnmodified?: boolean;
    fieldParameterMappings?: FieldParameterMapping[];
    callback?: (data: any) => void;
    backEventType?: BackEventType;
}

export interface TableActionDefinition extends ActionDefinition {
    enableWithoutTableData: boolean;
    column: string;
}

export interface ConfirmedActionDefinition extends ActionDefinition {
    confirmationMessage: I18nMessage;
    confirmationAccept?: I18nMessageDefinition;
    confirmationDeclineKey?: string;
}
interface TableCellActionDefinition {
    submitsTableData?: boolean;
    rowLevelQueue?: boolean;
}
export interface ServerActionDefinition extends ActionWithConstantsDefinition, ActionDefinition, TableCellActionDefinition {
    queueBehaviour?: "singleBlocking" | "onlyLast";
    url: string;
    httpMethod: string;
    fieldParameterMappings: FieldParameterMapping[];
    mapAll: boolean;
    successUpdatesParent: boolean;
    onBeforeDefaultResponse?: ((Response) => {});
}
export interface ActionWithConstantsDefinition {
    constants?: { string: string };
}
export interface BrowseActionDefinition extends ActionDefinition {
    url: string;
    target?: string;
    fieldParameterMappings: FieldParameterMapping[];
    mapAll: boolean;
}
export interface UnstackActionDefinition extends ActionDefinition {
    unstack: boolean;
}

export interface DataDispatchActionDefinition extends ActionWithConstantsDefinition, ActionDefinition {
    targetComponent: string;
    unstack: boolean;
}
export interface UndoActionDefinition extends ActionDefinition {
    undo: boolean;
}
export interface ParameterizedActionDefinition extends ActionDefinition {
    parametersForm: Bundle;
    confirmationAcceptKey?: string;
    confirmationDeclineKey?: string;
}

export interface CallbackActionDefinition extends ActionDefinition {
    callback: (data: any) => void;
}

export function isBrowseActionDefinition(arg: any): arg is ServerActionDefinition {
    return arg.url !== undefined && arg.actionType === "browse";
}
export function isServerActionDefinition(arg: any): arg is ServerActionDefinition {
    return arg.url !== undefined && (arg.actionType == null || arg.actionType === "server");
}
export function isActionWithConstantsDefinition(arg: any): arg is ActionWithConstantsDefinition {
    return arg.constants != null;
}
export function isDataDispatchActionDefinition(arg: any): arg is DataDispatchActionDefinition {
    return arg.actionType === "dataDispatch";
}
export function isCallbackActionDefinition(arg: any): arg is CallbackActionDefinition {
    return arg != null && arg.callback !== undefined;
}
export function isConfirmedActionDefinition(arg: any): arg is ConfirmedActionDefinition {
    return arg != null && arg.confirmationMessage !== undefined && arg.confirmationMessage !== null;
}
export function isUnstackActionDefinition(arg: any): arg is UnstackActionDefinition {
    return arg != null && arg.unstack === true;
}
export function isUndoActionDefinition(arg: any): arg is UndoActionDefinition {
    return arg != null && arg.undo === true;
}
export function isParameterizedActionDefinition(arg: any): arg is ParameterizedActionDefinition {
    return arg != null && arg.parametersForm !== undefined;
}
export function isTableActionDefinition(arg: any): arg is TableActionDefinition {
    return arg.enableWithoutTableData !== undefined || arg.column !== undefined;
}
